import { RSAA } from 'redux-api-middleware';
import { Dispatch } from 'redux';
import {
  TGetRootState,
  TAsyncDispatchResult,
  TDispatchResult,
} from 'types/shared';

const GET_REQUEST = 'OPUS/SELECTED_SOURCE_OF_STREAMS/GET_REQUEST';
const PUT_REQUEST = 'OPUS/SELECTED_SOURCE_OF_STREAMS/PUT_REQUEST';
const GET_SUCCESS = 'OPUS/SELECTED_SOURCE_OF_STREAMS/GET_SUCCESS';
const PUT_SUCCESS = 'OPUS/SELECTED_SOURCE_OF_STREAMS/PUT_SUCCESS';
const FAILURE = 'OPUS/SELECTED_SOURCE_OF_STREAMS/FAILURE';

type SelectedSourceOfStreamsActionTypes =
  | typeof GET_REQUEST
  | typeof PUT_REQUEST
  | typeof GET_SUCCESS
  | typeof PUT_SUCCESS
  | typeof FAILURE;

interface ISelectedSourceOfStreamsAction {
  type: SelectedSourceOfStreamsActionTypes;
  error?: boolean;
  payload: any;
}

export interface ISelectedSourceOfStreamsState {
  pending: boolean;
  fulfilled: boolean;
  rejected: boolean;
  reason: any;
  value: any;
}

const initialState: ISelectedSourceOfStreamsState = {
  pending: false,
  fulfilled: false,
  rejected: false,
  reason: null,
  value: null,
};

export default function reducer(
  state: ISelectedSourceOfStreamsState = initialState,
  action: ISelectedSourceOfStreamsAction
): ISelectedSourceOfStreamsState {
  switch (action.type) {
    case GET_REQUEST:
      return {
        ...state,
        pending: true,
        fulfilled: false,
        rejected: false,
      };
    case PUT_REQUEST:
      return {
        ...state,
        pending: true,
        fulfilled: false,
        rejected: false,
        value: action.payload,
      };
    case PUT_SUCCESS:
      // We do not update the on put success because we are updating optimistically
      // and the user might already have newer settings once the success comes in.
      // If the PUT call fails the user will see an error message in the DSP selector
      return {
        ...state,
        pending: false,
        fulfilled: true,
        rejected: false,
        reason: null,
      };
    case GET_SUCCESS:
      return {
        ...state,
        pending: false,
        fulfilled: true,
        rejected: false,
        reason: null,
        value: action.payload,
      };
    case FAILURE:
      return {
        ...state,
        pending: false,
        fulfilled: false,
        rejected: Boolean(action.error),
        reason: action.payload,
      };

    default:
      return state;
  }
}

export function saveSelectedSourceOfStreams(
  sourceOfStreams: any
): TAsyncDispatchResult {
  return async (dispatch: Dispatch, getState: TGetRootState): Promise<any> => {
    const { id } = getState().authentication.profile;
    const payload = sourceOfStreams;

    return await dispatch({
      [RSAA]: {
        types: [
          {
            type: PUT_REQUEST,
            payload: payload,
          },
          {
            type: PUT_SUCCESS,
            payload: payload,
          },
          FAILURE,
        ],
        endpoint: `/kv/user_${id}_selected_sourceOfStreams`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(payload),
      },
    });
  };
}

/**
 * This action creators load the users' currently selected sourceOfStreams, or
 * it creates a new record in the key/value store.
 */
export function loadSelectedSourceOfStreams(): TAsyncDispatchResult {
  return async function (
    dispatch: Dispatch,
    getState: TGetRootState
  ): Promise<any> {
    // get the user id
    const { id } = getState().authentication.profile;
    const { selectedSourceOfStreams } = getState();

    // fetch the users' selected sourceOfStreams
    const actionResponse: any = await dispatch({
      [RSAA]: {
        bailout:
          selectedSourceOfStreams.pending || selectedSourceOfStreams.fulfilled,
        types: [
          GET_REQUEST,
          {
            type: GET_SUCCESS,
            payload: async function payload(
              action: ISelectedSourceOfStreamsAction,
              state: ISelectedSourceOfStreamsState,
              res: any
            ): Promise<any> {
              const selectedSourceOfStreams = await res.json();

              return selectedSourceOfStreams;
            },
          },
          FAILURE,
        ],
        endpoint: `/kv/user_${id}_selected_sourceOfStreams`,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
      },
    });
    return actionResponse;
  };
}

export const shouldLoadSelectedSourceOfStreams =
  (): TDispatchResult<boolean> => {
    return (_: Dispatch, getState: TGetRootState): boolean => {
      const state = getState();
      const { selectedSourceOfStreams } = state;
      const { pending, value } = selectedSourceOfStreams;
      return !value && !pending;
    };
  };
